/*
 * PetriNet.ts
 * ===========
 * The Petri net formalism is used as the underlying representation for modeling progression of
 * task states. Places represent notable states in the task abstraction, and transitions represent
 * functions that perform work towards changing the task state.
 *
 * An extension is applied here whereby the tokens contained in places model (typed) data. A place
 * declares its type during construction. A marking indicates an array of data per place adhering
 * to those type restrictions. Transitions read and consume data from input places, and generate
 * data to add to output places. These data are modeled as Any protos.
 *
 * Transitions could represent units of action, interaction, observation, or arbitrary
 * computational processing.
 *
 * For synchronization to work properly across surfaces, these controllers should be modeled with
 * no other state beyond the graph marking. The graph marking includes typed data held in tokens.
 * Controllers may fire normally or be instructed to "snap" arbitrarily to a specific marking in
 * order to keep execution in sync with the same controller running on another surface.
 *
 * Petri nets can be linked together by connecting places and transitions across them. The PetriNet
 * internally tracks all places that are connected, but if those places belong to other Petri nets,
 * their tokens will not be included in the PetriNetState marking for a given Petri net so as not to
 * duplicate data. Firing of transitions depends on PetriNetStates from all connected Petri nets.
 * Because of this functionality, Petri nets need to be created with a globally unique ID.
 */
import { PetriNetState, PlaceData } from "tsproto/aware_proto/proto/AliveNet";
import { Any } from "tsproto/google/protobuf/any";
import ProtoUtils from "../ProtoUtils";
class Place {
    id;
    petriNet;
    type;
    inputTransitions;
    outputTransitions;
}
class Transition {
    id;
    petriNet;
    inputPlaces;
    outputPlaces;
    fn;
    weights;
    connectedPetriNetIds;
    getConnectedPlaces() {
        const inputPlaces = this.inputPlaces || [];
        const outputPlaces = this.outputPlaces || [];
        const places = [];
        for (const place of inputPlaces) {
            if (!places.includes(place)) {
                places.push(place);
            }
        }
        for (const place of outputPlaces) {
            if (!places.includes(place)) {
                places.push(place);
            }
        }
        return places;
    }
    //////// Utilities
    static getAllIndices(length) {
        return Array.from({ length: length }, (_, k) => k);
    }
    static clearAllInputs(inputData) {
        const indices = [];
        for (const array of inputData) {
            indices.push(Transition.getAllIndices(array.length));
        }
        return indices;
    }
    static keepAllInputs(inputData) {
        return new Array(inputData.length).fill([]);
    }
}
export default class PetriNet {
    static allPetriNetIds = [];
    id;
    places = {};
    transitions = {};
    transitionsEnabled = {};
    initialMarking = {};
    children = [];
    parent;
    constructor(id) {
        if (PetriNet.allPetriNetIds.includes[id]) {
            throw new Error(`PetriNet ${id} already defined! Choose another ID.`);
        }
        this.id = id;
        PetriNet.allPetriNetIds.push(id);
        this.places[id] = {};
    }
    dispose() {
        PetriNet.allPetriNetIds = PetriNet.allPetriNetIds.filter(id => id !== this.id);
    }
    definePlace(placeId, messageType) {
        if (placeId in this.places) {
            throw new Error(`Place ${placeId} is already defined`);
        }
        const place = new Place();
        place.id = placeId;
        place.type = messageType;
        place.petriNet = this;
        place.inputTransitions = [];
        place.outputTransitions = [];
        Object.freeze(place);
        this.places[this.id][placeId] = place;
        ProtoUtils.registerType(messageType);
        return place;
    }
    /**
     * In the firing function, input arcs are defined with negative numbers and output arcs with
     * positive numbers. A transition is enabled when all input places have tokens, but its firing may
     * move tokens into only a subset of the output places.
     *
     * @param weights The minimum number of tokens to count towards activation. Use negative numbers
     * to indicate inhibition logic.
     */
    defineTransition(transitionId, inputPlaces, weights, transitionFn, outputPlaces) {
        if (transitionId in this.transitions) {
            throw new Error(`Transition ${transitionId} is already defined`);
        }
        const errorPrefix = `Error defining transition ${transitionId}`;
        const connectedPetriNetIds = [];
        for (const place of inputPlaces) {
            const petriNetId = place.petriNet.id;
            if (!this.places[petriNetId]) {
                this.places[petriNetId] = {};
            }
            this.places[petriNetId][place.id] = place;
            if (!connectedPetriNetIds.includes(petriNetId)) {
                connectedPetriNetIds.push(petriNetId);
            }
        }
        for (const place of outputPlaces) {
            const petriNetId = place.petriNet.id;
            if (!this.places[petriNetId]) {
                this.places[petriNetId] = {};
            }
            this.places[petriNetId][place.id] = place;
            if (!connectedPetriNetIds.includes(petriNetId)) {
                connectedPetriNetIds.push(petriNetId);
            }
        }
        if (weights.length !== inputPlaces.length) {
            throw new Error(`${errorPrefix}: Length mismatch for weights (${weights.length} vs. ${inputPlaces.length})`);
        }
        if (!transitionFn.fire) {
            throw new Error(`${errorPrefix}: Transition fire() function not defined`);
        }
        const transition = new Transition();
        transition.id = transitionId;
        transition.petriNet = this;
        transition.inputPlaces = inputPlaces;
        transition.outputPlaces = outputPlaces;
        transition.fn = transitionFn;
        transition.weights = weights;
        transition.connectedPetriNetIds = connectedPetriNetIds;
        Object.freeze(transition);
        this.transitions[transitionId] = transition;
        this.transitionsEnabled[transitionId] = false;
        inputPlaces.forEach(place => place.outputTransitions.push(transition));
        outputPlaces.forEach(place => place.inputTransitions.push(transition));
        return transition;
    }
    defineInitialMarking(marking) {
        const errorPrefix = "Invalid initial marking";
        for (const placeId in marking) {
            const arrayData = marking[placeId];
            if (!Array.isArray(arrayData)) {
                throw new Error(`${errorPrefix}: ${placeId} data is not an array.`);
            }
            for (const value of arrayData) {
                if (!this.places[this.id][placeId].type.isAssignable(value)) {
                    throw new Error(`${errorPrefix}: Type mismatch for ${placeId} (${this.places[placeId]} \
            vs. ${typeof value}).`);
                }
            }
        }
        this.initialMarking = marking;
    }
    addChild(petriNet) {
        if (petriNet.parent) {
            throw new Error(`${petriNet.id} is already a child of ${petriNet.parent.id}`);
        }
        const childPetriNetIds = petriNet.getChildPetriNetIds();
        if (childPetriNetIds.includes(this.id)) {
            throw new Error(`Cycle detected. ${this.id} is a child of ${petriNet.id}`);
        }
        this.children.push(petriNet);
        petriNet.parent = this;
    }
    removeChild(petriNet) {
        this.children = this.children.filter(v => v !== petriNet);
        if (petriNet.parent && petriNet.parent === this) {
            petriNet.parent = undefined;
        }
    }
    isChild(petriNetId) {
        if (this.children.length === 0)
            return false;
        if (this.children.find(v => v.id === petriNetId))
            return true;
        for (const petriNet of this.children) {
            if (petriNet.isChild(petriNetId)) {
                return true;
            }
        }
        return false;
    }
    getRoot() {
        if (!this.parent) {
            return this;
        }
        return this.parent.getRoot();
    }
    isTransitionEnabled(transition, states) {
        if (!(transition?.id in this.transitions)) {
            throw new Error(`isTransitionEnabled: Transition ${transition?.id} not defined in this Petri net`);
        }
        if (this.transitions[transition.id] !== transition) {
            throw new Error(`isTransitionEnabled: Transition ${transition.id} mismatch`);
        }
        for (let i = 0; i < transition.inputPlaces.length; ++i) {
            const inputPlace = transition.inputPlaces[i];
            const weight = transition.weights[i];
            if (weight < 0) {
                // Inhibitor arc
                if (this.getNumberOfTokens(inputPlace, states) >= Math.abs(weight)) {
                    return false;
                }
            }
            else {
                if (this.getNumberOfTokens(inputPlace, states) < weight) {
                    return false;
                }
            }
        }
        return true;
    }
    getTransition(firingFn) {
        return Object.values(this.transitions).find(t => t.fn === firingFn);
    }
    getPlaceIds() {
        return Object.keys(this.places);
    }
    getTransitionIds() {
        return Object.keys(this.transitions);
    }
    /**
     * Get the IDS of all Petri nets relevant to this Petri net's execution. Includes itself, its
     * children, and other Petri nets that are dependencies of itself and its children.
     */
    getPetriNetIds() {
        const petriNetIds = Object.keys(this.places);
        for (const child of this.children) {
            for (const id of child.getPetriNetIds()) {
                if (!petriNetIds.includes(id)) {
                    petriNetIds.push(id);
                }
            }
        }
        return petriNetIds;
    }
    /**
     * Get the IDs of all Petri nets that this Petri net depends on, including deep dependencies of
     * children.
     */
    getDependencyPetriNetIds() {
        let petriNetIds = Object.keys(this.places);
        if (this.parent && !petriNetIds.includes(this.parent?.id)) {
            petriNetIds.push(this.parent.id);
        }
        for (const child of this.children) {
            for (const id of child.getDependencyPetriNetIds()) {
                if (!petriNetIds.includes(id)) {
                    petriNetIds.push(id);
                }
            }
        }
        petriNetIds = petriNetIds.filter(v => v !== this.id);
        petriNetIds = petriNetIds.filter(v => !this.getChildPetriNetIds().includes(v));
        return petriNetIds;
    }
    /**
     * Get the IDs of all child Petri nets (deeply).
     */
    getChildPetriNetIds() {
        const petriNetIds = [];
        for (const child of this.children) {
            if (!petriNetIds.includes(child.id)) {
                petriNetIds.push(child.id);
            }
            for (const id of child.getChildPetriNetIds()) {
                if (!petriNetIds.includes(id)) {
                    petriNetIds.push(id);
                }
            }
        }
        return petriNetIds;
    }
    hasTokens(place, states) {
        return this.getNumberOfTokens(place, states) > 0;
    }
    getNumberOfTokens(place, states) {
        const petriNetId = place.petriNet.id;
        const state = states[petriNetId];
        return state?.marking[place.id]?.tokens?.length || 0;
    }
    getUnpackedData(place, states) {
        const petriNetId = place.petriNet.id;
        const placeId = place.id;
        const marking = states[petriNetId]?.marking;
        if (!marking || !marking[placeId]?.tokens)
            return [];
        return marking[placeId].tokens.map(token => {
            return Any.unpack(token, this.places[petriNetId][placeId].type);
        });
    }
    static getTokenValues(placeId, marking) {
        if (!marking || !marking[placeId]?.tokens)
            return [];
        return marking[placeId].tokens.map(token => {
            const messageType = ProtoUtils.messageType(token.typeUrl);
            return Any.unpack(token, messageType);
        });
    }
    getPackedData(place, data) {
        const petriNetId = place.petriNet.id;
        const placeId = place.id;
        if (!data || data.length === 0)
            return [];
        return data.map(datum => {
            return Any.pack(datum, this.places[petriNetId][placeId].type);
        });
    }
    getPlaceData(place, data) {
        const placeData = PlaceData.create();
        placeData.tokens = this.getPackedData(place, data);
        return placeData;
    }
    setDataInMarking(place, data, marking) {
        marking[place.id] = this.getPlaceData(place, data);
    }
    // Can fire a maximum of one transition at a time
    step(states) {
        // Ensure transition enabled states are set correctly
        this.synchronize(states);
        // Attempt a firing
        for (const transition of Object.values(this.transitions)) {
            const [fired, changedPlaces, nextStates] = this.stepTransition(transition, states);
            if (fired) {
                return [fired, transition, changedPlaces, nextStates];
            }
        }
        return [false];
    }
    stepTransition(transition, states) {
        // Ensure transition enabled states are set correctly
        this.synchronizeTransition(transition, states);
        // Attempt a firing
        if (this.transitionsEnabled[transition.id]) {
            const [fired, changedPlaces, nextStates] = this.fireTransition(transition.id, states);
            if (fired) {
                if (transition.fn.onFire) {
                    transition.fn.onFire(transition);
                }
                // Ensure transition enabled states are set correctly for the next state
                this.synchronize(nextStates);
                if (changedPlaces.some(place => transition.inputPlaces.includes(place))) {
                    this.runOnInputChange(states, nextStates);
                }
                return [fired, changedPlaces, nextStates];
            }
        }
        return [false, [], PetriNet.clonePetriNetStates(states)];
    }
    // Check for changes to input places that didn't result in a change in enabling
    runOnInputChange(prevStates, nextStates) {
        for (const transition of Object.values(this.transitions)) {
            if (!transition.fn.onInputChange)
                continue;
            if (this.isTransitionEnabled(transition, prevStates) &&
                this.isTransitionEnabled(transition, nextStates)) {
                const prevInput = this.getInputData(transition.id, prevStates);
                const nextInput = this.getInputData(transition.id, nextStates);
                let changed = false;
                for (let i = 0; i < transition.inputPlaces.length; ++i) {
                    const inputPlace = transition.inputPlaces[i];
                    if (prevInput[i].length !== nextInput[i].length) {
                        changed = true;
                        break;
                    }
                    for (let j = 0; j < prevInput[i].length; ++j) {
                        const prevValue = prevInput[i][j];
                        const nextValue = nextInput[i][j];
                        if (!inputPlace.type.equals(prevValue, nextValue)) {
                            changed = true;
                            break;
                        }
                    }
                    if (changed)
                        break;
                }
                if (changed) {
                    transition.fn.onInputChange(transition, prevInput, nextInput);
                }
            }
        }
    }
    getInputData(transitionId, states) {
        const transition = this.transitions[transitionId];
        return transition.inputPlaces.map(place => { return this.getUnpackedData(place, states); });
    }
    getOutputData(transitionId, states) {
        const transition = this.transitions[transitionId];
        return transition.outputPlaces.map(place => { return this.getUnpackedData(place, states); });
    }
    fireTransition(transitionId, states) {
        const transition = this.transitions[transitionId];
        if (!this.isTransitionEnabled(transition, states) || !this.transitionsEnabled[transitionId]) {
            console.warn(`${this.constructor.name} Trying to fire transition ${transitionId} that's not enabled!`);
            return [false, [], states];
        }
        const prevInputData = transition.inputPlaces.map(place => { return this.getUnpackedData(place, states); });
        const prevOutputData = transition.outputPlaces.map(place => { return this.getUnpackedData(place, states); });
        const [fired, inputIndices, outputDataToAdd] = transition.fn.fire(transition, prevInputData, prevOutputData);
        if (!fired) {
            return [false, [], states];
        }
        // Verify firing result
        const errorPrefix = `Error firing transition ${transitionId} in ${this.id}`;
        if (inputIndices?.length !== transition.inputPlaces.length) {
            throw new Error(`${errorPrefix}: Input indices length mismatch: ${inputIndices?.length} ` +
                `provided, ${transition.inputPlaces.length} expected`);
        }
        if (outputDataToAdd?.length !== transition.outputPlaces.length) {
            throw new Error(`${errorPrefix}: Output data length mismatch: ${outputDataToAdd?.length} ` +
                `provided, ${transition.outputPlaces.length} expected`);
        }
        // Compute next marking from firing function
        const nextStates = PetriNet.clonePetriNetStates(states);
        const changedPlaces = [];
        // Remove consumed tokens from input places
        for (let i = 0; i < transition.inputPlaces.length; ++i) {
            if (!inputIndices[i] || inputIndices[i].length === 0)
                continue;
            const inputPlace = transition.inputPlaces[i];
            changedPlaces.push(inputPlace);
            const indicesToRemove = inputIndices[i].sort((a, b) => b - a);
            const petriNetId = inputPlace.petriNet.id;
            const nextState = nextStates[petriNetId];
            const tokens = (nextState?.marking && nextState.marking[inputPlace.id]?.tokens) || [];
            for (const index of indicesToRemove) {
                tokens.splice(index, 1);
            }
        }
        // Add newly generated output data to output places
        for (let i = 0; i < transition.outputPlaces.length; ++i) {
            if (!outputDataToAdd[i] || outputDataToAdd[i].length === 0)
                continue;
            const outputPlace = transition.outputPlaces[i];
            if (!changedPlaces.includes(outputPlace)) {
                changedPlaces.push(outputPlace);
            }
            const petriNetId = outputPlace.petriNet.id;
            let nextState = nextStates[petriNetId];
            if (!nextState) {
                nextState = this.createState();
                nextStates[petriNetId] = nextState;
            }
            if (!nextState.marking) {
                nextState.marking = {};
            }
            if (!nextState.marking[outputPlace.id]) {
                nextState.marking[outputPlace.id] = PlaceData.create();
            }
            if (!nextState.marking[outputPlace.id]?.tokens) {
                nextState.marking[outputPlace.id].tokens = [];
            }
            const outputData = this.getPackedData(outputPlace, outputDataToAdd[i]);
            nextState.marking[outputPlace.id].tokens.push(...outputData);
        }
        Object.values(nextStates).forEach(nextState => {
            PetriNet.stripZeros(nextState.marking);
        });
        return [true, changedPlaces, nextStates];
    }
    static stripZeros(marking) {
        for (const [placeId, placeData] of Object.entries(marking)) {
            if (!placeData.tokens || placeData.tokens.length === 0) {
                delete marking[placeId];
            }
        }
        return marking;
    }
    createState() {
        return PetriNetState.create({
            petriNetId: this.id,
            marking: {}
        });
    }
    createInitialState() {
        const marking = {};
        for (const placeId in this.initialMarking) {
            const placeData = PlaceData.create();
            placeData.tokens = this.getPackedData(this.places[this.id][placeId], this.initialMarking[placeId]);
            marking[placeId] = placeData;
        }
        return PetriNetState.create({
            petriNetId: this.id,
            marking: marking
        });
    }
    synchronize(states) {
        if (!states)
            return;
        for (const transition of Object.values(this.transitions)) {
            this.synchronizeTransition(transition, states);
        }
        // TODO is this correct?
        for (const child of this.children) {
            child.synchronize(states);
        }
    }
    synchronizeTransition(transition, states) {
        if (!states)
            return;
        if (!(transition?.id in this.transitions)) {
            throw new Error(`synchronizeTransition: Transition ${transition?.id} not defined in this Petri net`);
        }
        if (this.transitions[transition.id] !== transition) {
            throw new Error(`synchronizeTransition: Transition ${transition.id} mismatch`);
        }
        const transitionId = transition.id;
        const prevEnabled = this.transitionsEnabled[transitionId];
        if (this.isTransitionEnabled(transition, states)) {
            if (!prevEnabled) {
                if (transition.fn.onEntry) {
                    const inputData = this.getInputData(transitionId, states);
                    const outputData = this.getOutputData(transitionId, states);
                    transition.fn.onEntry(transition, inputData, outputData);
                }
                this.transitionsEnabled[transitionId] = true;
            }
        }
        else {
            if (prevEnabled) {
                if (transition.fn.onExit) {
                    transition.fn.onExit(transition);
                }
                this.transitionsEnabled[transitionId] = false;
            }
        }
    }
    // PetriNetState.equals(...) for comparing states is NOT correct for our implementation!
    static areStatesEqual(state1, state2, placeIds) {
        if (state1 === state2)
            return true;
        if (state1?.petriNetId !== state2?.petriNetId)
            return false;
        return PetriNet.areMarkingsEqual(state1?.marking, state2?.marking, placeIds);
    }
    static isMarkingEmpty(marking) {
        if (!marking)
            return true;
        if (Object.keys(marking).length === 0)
            return true;
        for (const placeId in marking) {
            if (marking[placeId]?.tokens?.length > 0) {
                return false;
            }
        }
        return true;
    }
    static countTokens(marking) {
        if (!marking)
            return 0;
        if (Object.keys(marking).length === 0)
            return 0;
        let count = 0;
        for (const placeId in marking) {
            count += marking[placeId]?.tokens?.length || 0;
        }
        return count;
    }
    // We take undefined markings to be equivalent to empty markings
    static areMarkingsEqual(marking1, marking2, placeIds) {
        if (marking1 === marking2)
            return true;
        const marking1Empty = PetriNet.isMarkingEmpty(marking1);
        const marking2Empty = PetriNet.isMarkingEmpty(marking2);
        if (marking1Empty && marking2Empty)
            return true;
        if (marking1Empty !== marking2Empty)
            return false;
        if (!placeIds) {
            placeIds = Array.from(new Set([Object.keys(marking1), Object.keys(marking2)].flat()));
        }
        for (const placeId of placeIds) {
            const placeData1 = marking1[placeId];
            const placeData2 = marking2[placeId];
            const length1 = placeData1?.tokens?.length || 0;
            const length2 = placeData2?.tokens?.length || 0;
            if (length1 !== length2) {
                return false;
            }
            for (let i = 0; i < length1; ++i) {
                const token1 = placeData1.tokens[i];
                const token2 = placeData2.tokens[i];
                if (!Any.equals(token1, token2)) {
                    return false;
                }
            }
        }
        return true;
    }
    static clonePetriNetStates(states) {
        const result = {};
        Object.keys(states).forEach(petriNetId => {
            const state = states[petriNetId];
            result[petriNetId] = PetriNetState.clone(state);
        });
        return result;
    }
    static merge(state1, state2) {
        const mergedState = PetriNetState.clone(state1);
        if (state2.marking) {
            for (const placeId in state2.marking) {
                const placeData2 = state2.marking[placeId];
                const length = placeData2?.tokens?.length || 0;
                if (length > 0) {
                    for (let i = 0; i < length; ++i) {
                        const token = placeData2.tokens[i];
                        if (!mergedState.marking) {
                            mergedState.marking = {};
                        }
                        if (!mergedState.marking[placeId]) {
                            mergedState.marking[placeId] = PlaceData.create({ tokens: [] });
                        }
                        if (mergedState.marking[placeId].tokens) {
                            if (!mergedState.marking[placeId].tokens.some(v => Any.equals(v, token))) {
                                mergedState.marking[placeId].tokens.push(token);
                            }
                        }
                        else {
                            mergedState.marking[placeId].tokens = [token];
                        }
                    }
                }
            }
        }
        return mergedState;
    }
}
/**
 * A firing function that retains only the input tokens that match the filter function, and
 * removes the rest.
 */
function FilterFiringFn(filterFn, inputPlaceIndex) {
    return (transition, inputData, outputData) => {
        const inputs = inputData[inputPlaceIndex || 0];
        const indicesToRemove = [];
        for (let i = 0; i < inputs.length; ++i) {
            const input = inputs[i];
            if (!filterFn(input)) {
                indicesToRemove.push(i);
            }
        }
        if (indicesToRemove.length > 0) {
            return [true, [indicesToRemove], []];
        }
        return [false];
    };
}
export { Place, Transition, FilterFiringFn };
